import {
    PLUS,
    MINUS,
    TIMES,
    SLASH,
    PERCENT,
    HAT,
    NUMBER,
    STRING,
    LEFT_PAREN,
    RIGHT_PAREN,
    EOF,
    NEWLINE,
    LEFT_ARROW,
    RIGHT_ARROW,
    IDENTIFIER,
    DOT,
    COMMA,
    RIGHT_PIPE,
    LEFT_PIPE,
    HIDE,
    FORMAT_AS_FLOAT,
    FORMAT_AS_RATIO,
    LEFT_BRACE,
    FUNCTION,
    RIGHT_BRACE,
} from './lex.js';

export const SOLO_OUTPUT = "solo-output"

const debugLog = (...things) => {
    if (true) { return }
    console.log(...things)
}

const makeBinary = (operator, left, right) => ({
    type: "binary", operator, left, right,
})

const makeUnary = (operator, right) => ({
    type: "unary", operator, right,
})

const makeGrouping = (value) => ({
    type: "grouping", value,
})

const makeString = ({ body }) => ({
    type: "literal-string", body,
})

const makeNumber = ({ numerator, denominator}) => ({
    type: "literal-number", numerator, denominator,
})

const makeStorage = (name, value) => ({
    type: "storage", name, value,
})

const makeRetrieval = (name) => ({
    type: "retrieval", name,
})

const makeInvocation = (path, args) => ({
    type: "invocation", path, args,
})

const makeHide = (name) => ({
    type: "hide", name,
})

const makeFormat = (name, style) => ({
    type: "format", name, style,
})

const makeFunction = (name, {statements, expr}) => ({
    type: "function", name, bodyStatements: statements, returnExpression: expr,
})

export const parse = (originalTokens, allow_solo_expression = false) => {
    const FC_Error = (message, ...args) => {
        return new Error(`parsing token ${current}: ${message}`, ...args)
    }

    let indicesToBeDeleted = []
    originalTokens.forEach((_t, i) => {
        if (originalTokens[i]?.type === NEWLINE && [RIGHT_PIPE, RIGHT_ARROW].includes(originalTokens[i + 1]?.type)) {
            indicesToBeDeleted.push(i)
        }
    })
    const tokens = originalTokens.flatMap((t, i) => {
        if (indicesToBeDeleted.includes(i)) {
            return []
        } else {
            return [t]
        }
    })

    let current = 0;

    const statement = ({context = "program"}) => {
        window.postALog(`looking for a statement in context of ${context}`)

        if (matchList([IDENTIFIER, LEFT_ARROW])) {
            const [name, _arrow] = advanceList(2)
            debugLog(`found: left store to '${name.value}'`)
            window.postALog(`found left store to '${name.value}'`)
            return makeStorage(name, expression())
        }

        if (context === "program") {
            if (matchList([HIDE, IDENTIFIER])) {
                const [_hide, name] = advanceList(2)
                debugLog(`found: hide id '${name.value}'`)
                return makeHide(name.value)
            }    
            
            if (matchList([FORMAT_AS_FLOAT, IDENTIFIER])) {
                const [_format, name] = advanceList(2)
                debugLog(`found: format as float id '${name.value}'`)
                return makeFormat(name.value, FORMAT_AS_FLOAT)
            }

            if (matchList([FORMAT_AS_RATIO, IDENTIFIER])) {
                const [_format, name] = advanceList(2)
                debugLog(`found: format as ratio id '${name.value}'`)
                return makeFormat(name.value, FORMAT_AS_RATIO)
            }
            

            if (matchList([FUNCTION, IDENTIFIER, LEFT_BRACE])) {
                const [_function, name, _brace] = advanceList(3)
                debugLog(`found: function id '${name.value}' `)
                window.postALog(`found a function named '${name.value}'`)
                const fun_body = function_body()

                if (fun_body.expr === undefined) {
                    throw new FC_Error(`expected exactly 1 bare expression in function body`)
                }

                return makeFunction(name.value, fun_body)
            }
        }

        const expr = expression()
        if (matchList([RIGHT_ARROW, IDENTIFIER])) {
            const [_arrow, name] = advanceList(2)
            debugLog(`found: right store to '${name.value}'`)
            return makeStorage(name, expr)
        }

        if (allow_solo_expression) {
            return makeStorage({value: SOLO_OUTPUT}, expr);
        }

        throw new FC_Error(`didn't find a statement`)
    }

    const expression = () => {
        window.postALog(`[current: ${current}] looking for expression`)

        if (match(NEWLINE)) {
            debugLog(`returning expression after finding a NEWLINE`)
            window.postALog(`[current: ${current}] found newline`)
            return expression()
        }

        let expr = operand()

        if (match(PLUS, MINUS, TIMES, SLASH, PERCENT, HAT)) {
            window.postALog(`[current: ${current}] found binary: ${previous().type}`)
            const operator = previous()
            const right = operand()
            expr = makeBinary(operator, expr, right)
        } else if (peek().type === RIGHT_PIPE) {
            while (match(RIGHT_PIPE)) {
                const right = primary()
                if (right.type !== "invocation") {
                    throw new FC_Error("expecting an invocation after a rightwards pipe")
                }
                expr = makeInvocation(right.path, [expr, ...right.args])
            }
        } else if (match(LEFT_PIPE)) {
            const right = expression()
            if (expr.type !== "invocation") {
                throw new FC_Error("expecting an invocation before a leftwards pipe")
            }
            expr.args = [right, ...expr.args]
        }

        window.postALog(`returning a ${expr.type}`)
        debugLog(`returning a ${expr.type}: ${expr.operator?.type ?? expr.value ?? ""}`)
        return expr
    }

    const operand = () => {
        window.postALog(`[current: ${current}] looking for operand`)
        if (match(PLUS, MINUS)) {
            const operator = previous()
            const right = primary()
            return makeUnary(operator, right)
        } else {
            return primary()
        }
    }

    const primary = () => {
        window.postALog(`[current: ${current}] looking for primary`)
        
        if (match(STRING)) {
            window.postALog(`[current: ${current}] found a string`)
            debugLog(`found string: ${JSON.stringify(previous())}`)
            return makeString({ body: previous().body})
        } else if (match(NUMBER)) {
            window.postALog(`[current: ${current}] found a number`)
            debugLog(`found number: ${JSON.stringify(previous())}`)
            return makeNumber({ numerator: previous().numerator, denominator: previous().denominator })
        } else if (match(IDENTIFIER)) {
            window.postALog(`[current: ${current}] found an identifier`)
            const path = []
            path.push(previous().value)
            while (peek(1).type === IDENTIFIER && match(DOT)) {
                path.push(advance().value)
            }

            if (match(LEFT_PAREN)) {
                window.postALog(`[current: ${current}] found an invocation`)
                debugLog(`looking for args for '${path}'`)
                const args = invocationArguments([])
                return makeInvocation(path, args)
            }

            debugLog(`found: retrieval from '${path.join(".")}'`)
            return makeRetrieval(path.join("."))
        } else {
            if (match(LEFT_PAREN)) {
                window.postALog(`[current: ${current}] found a grouping`)
                const expr = expression()
                consume(RIGHT_PAREN, "Expect ')' after expression.")
                return makeGrouping(expr)
            }
        }

        window.postALog(`[current: ${current}] found nothing`)

        throw new FC_Error("something went wrong")
    }

    const invocationArguments = (args = []) => {
        if (args.length > 0) debugLog(`looking for args after the first ${args.length} arg(s)`)

        if (match(RIGHT_PAREN)) {
            return args
        }

        const expr = expression()
        debugLog(`found arg of type ${expr.type}`)
        const newArgs = [...args, expr]

        if (match(RIGHT_PAREN)) {
            return newArgs
        } else {
            consume(COMMA, "Expect ',' after argument.")
            return invocationArguments([...args, expr])
        }
    }

    const function_body = (statements_so_far = [], expr = undefined) => {
        while (check(NEWLINE)) {
            advance()
        }

        if (check(RIGHT_BRACE)) {
            advance()

            return { statements: statements_so_far, expr: expr }
        }

        let kind = null
        let thisExpressionOrStatement = null

        window.postALog(`reading body, so far: ${statements_so_far.length} statements and ${expr === null ? 1 : 0} expression(s)`)

        let bookmark = current
        try {
            window.postALog(`[current: ${current}] [body] looking for statement`)
            thisExpressionOrStatement = statement({context: "function"})
            kind = "statement"
        } catch(e) {
            current = bookmark
            if (e.message.includes("didn't find a statement"))
            window.postALog(`[current: ${current}] [body] looking for expression`)
            thisExpressionOrStatement = expression()
            kind = "expression"
            if (expr !== undefined) {
                throw new FC_Error(`found a second expression in function body`)
            }
        }

        if (kind === "statement") {
            return function_body([...statements_so_far, thisExpressionOrStatement], expr)
        } else if (kind === "expression") {
            return function_body(statements_so_far, thisExpressionOrStatement)
        }
    }

    const match = (...tokenTypes) => {
        for (const type of tokenTypes) {
            if (check(type)) {
                advance()
                return true
            }
        }
        return false
    }

    const advance = () => {
        if (!isAtEnd()) {
            current += 1
        }
        return previous()
    }

    const check = (tokenType) => {
        if (isAtEnd()) { return false }

        return tokenType === peek().type
    }

    const isAtEnd = () => {
        return peek().type === EOF
    }

    const peek = (count = 0) => {
        return tokens[current + count]
    }

    const previous = () => {
        return tokens[current - 1]
    }

    const consume = (tokenType, errorMessage) => {
        if (match(tokenType)) {
            return true
        } else {
            throw errorMessage
        }
    }

    const matchList = (tokens) => {
        if (isAtEnd()) { return false }

        return tokens.every((token, index) => {
            return (peek(index).type === token)
        })
    }

    const advanceList = (count) => (
        Array(count).fill(null).map(() => (
            advance()
        ))
    )

    let statements = []
    let missingStatementErrorCount = 0
    while (!isAtEnd()) {
        if (check(NEWLINE)) {
            advance()
            continue
        }
        try {
        const thisStatement = statement({context: "program"})
        consume(NEWLINE, `expected a newline to separate statements, instead found: ${peek()?.type ?? "unknown"}`)
        statements.push(thisStatement)
        } catch (e) {
            if (e.message?.includes("didn't find a statement")) {
                missingStatementErrorCount += 1
            } else {
                throw e
            }
        }
    }

    if (missingStatementErrorCount === 1) {
        return parse(originalTokens, true)
    }

    return statements
}
